import React, {ReactNode, useCallback, useEffect, useMemo, useState,} from "react";
import {
    Dropdown,
    getFocusStyle,
    IButtonProps,
    IconButton,
    ISelectableOption,
    Label,
    List,
    mergeStyleSets,
    Stack,
    Text,
} from "@fluentui/react";
import {SharedColors} from "@fluentui/theme";
import {useTranslation} from "react-i18next";
import {ShowApiError} from "./ShowApiError";
import {theme} from "../index";
import "../styles/DropdownFromList.css";
import {useErrorMessage} from "../hooks/useErrorMessage";

interface IDropdownFromList<T> {
    inline?: boolean;
    toKND: (t: T) => { key: any; name: string; data: T };
    id?: string;
    list: T[];
    setValue?: (t?: T) => void;
    value: any;
    label?: string;
    iconFromValue?: (t: T, item?: { key: string; name: string; data: T }) => any;
    showEmpty?: boolean;
    errorMessage?: string;
    disabled?: boolean;
    rightSideIconProps?: IButtonProps;
    overrideMinWidth?: number;
    noPadLabels?: boolean;
    className?: string;
    internal?: boolean;
    hiddenInputProps?: any;
    title?: string;
}

export function DropdownFromList<T>({
                                        internal,
                                        noPadLabels,
                                        className,
                                        showEmpty,
                                        inline,
                                        errorMessage,
                                        label,
                                        list,
                                        disabled,
                                        toKND,
                                        hiddenInputProps,
                                        id,
                                        setValue,
                                        value,
                                        iconFromValue,
                                        rightSideIconProps,
                                        overrideMinWidth,
                                        title,
                                    }: IDropdownFromList<T>) {
    const {t} = useTranslation();
    const [valueLocal, setValueLocal] = useState(value);
    const {localErrorMessage, clearLocalErrorMessage} =
        useErrorMessage(errorMessage);

    useEffect(() => {
        setValueLocal(value);
    }, [value]);

    const valueChanged = useCallback(
        (v?: T) => {
            setValueLocal(v);
            setValue?.(v);
        },
        [setValue, setValueLocal],
    );

    const classNames = useMemo(
        () =>
            mergeStyleSets({
                itemCell: [
                    getFocusStyle(theme, {inset: -1}),
                    {
                        padding: 10,
                        boxSizing: "border-box",
                        borderBottom: `1px solid ${theme.semanticColors.bodyDivider}`,
                        display: "flex",
                        selectors: {
                            "&:hover": {
                                background: theme.palette?.neutralLighter,
                                cursor: "pointer",
                            },
                        },
                        minWidth: 200,
                    },
                ],
                selectedItemCell: [
                    getFocusStyle(theme, {inset: -1}),
                    {
                        background: theme.palette.neutralLight,
                        padding: "10px 10px 10px 8px",
                        boxSizing: "border-box",
                        borderBottom: `1px solid ${theme.semanticColors.bodyDivider}`,
                        borderRight: `2px solid ${theme.palette.themePrimary}`,
                        display: "flex",
                        selectors: {
                            "&:hover": {cursor: "pointer"},
                        },
                        minWidth: 200,
                    },
                ],
                clearItemCell: [
                    getFocusStyle(theme, {inset: -1}),
                    {
                        padding: 10,
                        boxSizing: "border-box",
                        borderBottom: `1px solid ${theme.semanticColors.bodyDivider}`,
                        display: "flex",
                        selectors: {
                            "&:hover": {
                                background: theme.palette?.neutralLighter,
                                cursor: "pointer",
                            },
                            span: {
                                color: SharedColors.red10,
                                fontWeight: "bold",
                            },
                        },
                        minWidth: 200,
                        fontWeight: "bold !important",
                    },
                ],
            }),
        [],
    );

    const renderListCell = useCallback(
        (
            item?: { key: string; name: string; data: T } | undefined,
            index?: number,
            isScrolling?: boolean,
        ): ReactNode => {
            return (
                <Stack
                    horizontal
                    // tokens={{ childrenGap: 10 }}
                    verticalAlign={noPadLabels ? "center" : undefined}
                    horizontalAlign={"space-between"}
                    onClick={disabled ? undefined : () => valueChanged(item?.data)}
                    style={{
                        flex: 1,
                        width: "100%",
                        color: item?.data === undefined ? SharedColors.red10 : undefined,
                        ...(noPadLabels ? {padding: "0px"} : {}),
                    }}
                    className={
                        item?.data === undefined
                            ? classNames.clearItemCell
                            : item?.key === valueLocal || item?.data === valueLocal
                                ? classNames.selectedItemCell
                                : classNames.itemCell
                    }
                >
                    <Text
                        style={{
                            ...(noPadLabels ? {padding: "10px 10px 10px 8px"} : {}),
                        }}
                        key={item?.key}
                    >
                        {t(item?.name ?? "")}
                    </Text>
                    {iconFromValue ? iconFromValue(valueLocal, item) : null}
                </Stack>
            );
        },
        [
            noPadLabels,
            disabled,
            classNames.clearItemCell,
            classNames.selectedItemCell,
            classNames.itemCell,
            valueLocal,
            t,
            iconFromValue,
            valueChanged,
        ],
    );

    const onRenderOption = useCallback(
        (
            props?: ISelectableOption,
            defaultRender?: (p: ISelectableOption) => JSX.Element | null,
        ) => {
            const additionalProps = {
                color: props?.data === undefined ? SharedColors.red10 : undefined,
                fontWeight: props?.data === undefined ? "bold" : undefined,
            };

            return (
                <Stack
                    className={"dropdown-enum-item"}
                    horizontal
                    styles={{
                        root: {flex: 1, width: "100%", flexGrow: 1, minWidth: 300},
                        inner: {flex: 1, width: "100%", flexGrow: 1, minWidth: 300},
                    }}
                    color={props?.data === undefined ? SharedColors.red10 : undefined}
                    style={{
                        flex: 1,
                        width: "100%",
                        flexGrow: 1,
                        marginLeft: undefined,
                        minWidth: 300,
                    }}
                    horizontalAlign={"space-between"}
                    // onClick={() => valueChanged(props?.data as any)}
                >
                    <Text key={props?.key} style={additionalProps}>
                        {props?.text}
                    </Text>
                    {iconFromValue ? iconFromValue(props?.data) : null}
                </Stack>
            );
        },
        [iconFromValue, valueChanged],
    );

    if (inline) {
        return (
            <>
                <List
                    title={title}
                    aria-disabled={disabled}
                    id={id}
                    items={[
                        ...list.map(toKND),
                        ...((showEmpty
                            ? [
                                {
                                    text: t("clear"),
                                    key: undefined,
                                    data: undefined,
                                    name: t("clear"),
                                },
                            ]
                            : []) as any),
                    ]}
                    onRenderCell={renderListCell}
                />
                {localErrorMessage ? (
                    <ShowApiError errors={{"": [localErrorMessage]}} keys={[""]}/>
                ) : null}
            </>
        );
    }

    return (
        <Stack
            title={title}
            styles={{root: {flex: 1, minWidth: "100%"}}}
            horizontalAlign="stretch"
            className={className ?? "bropdown-from-list-element-base"}
        >
            {!!label && <Label>{label}</Label>}
            <Stack
                title={title}
                horizontal
                style={{flex: 1}}
                tokens={{childrenGap: 10}}
                styles={{root: {flex: 1, minWidth: "100%"}}}
            >
                <Dropdown
                    title={title}
                    disabled={disabled}
                    placeholder={label}
                    errorMessage={localErrorMessage}
                    onRenderOption={onRenderOption}
                    dropdownWidth={300}
                    onFocus={(e) => {
                        clearLocalErrorMessage();
                    }}
                    onDismiss={() => {
                    }}
                    styles={{
                        dropdown: {flex: 1, width: "100%", flexGrow: 1},
                        callout: {flex: 1, width: "100%", flexGrow: 1},
                        dropdownItem: {flex: 1, width: "100%", flexGrow: 1},
                        root: {flex: 1, width: "100%", flexGrow: 1},
                        dropdownItems: {flex: 1, width: "100%", flexGrow: 1},
                    }}
                    options={[
                        ...list.map((m) => {
                            const knd = toKND(m);
                            return {...knd, text: knd.name};
                        }),
                        ...((showEmpty
                            ? [
                                {
                                    text: t("clear"),
                                    key: undefined,
                                    data: undefined,
                                    name: t("clear"),
                                },
                            ]
                            : []) as any),
                    ]}
                    style={{minWidth: overrideMinWidth ?? 200}}
                    selectedKey={
                        (valueLocal as any)?.id ??
                        (valueLocal as any)?.key ??
                        valueLocal ??
                        ""
                    }
                    onChange={(event, option) => valueChanged(option?.data)}
                />
                {rightSideIconProps ? <IconButton {...rightSideIconProps} /> : null}
            </Stack>
            {hiddenInputProps ? (
                <input
                    hidden
                    {...hiddenInputProps}
                    value={
                        (valueLocal as any)?.id ??
                        (valueLocal as any)?.key ??
                        valueLocal ??
                        ""
                    }
                    onChange={() => {
                    }}
                />
            ) : null}
        </Stack>
    );
}
